import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FiBriefcase,
  FiFileText,
  FiZap,
  FiCalendar,
  FiPlus,
  FiGrid,
  FiShoppingBag,
  FiLayers,
  FiCheckCircle,
  FiShield,
  FiArrowRight,
  FiMapPin,
  FiMaximize,
  FiTag,
  FiAlertCircle,
} from "react-icons/fi";
import SaaSStatCard from "../../common/saas/SaaSStatCard";
import SaaSEmptyState from "../../common/saas/SaaSEmptyState";
import SaaSLoadingSkeleton from "../../common/saas/SaaSLoadingSkeleton";
import SaaSSectionHeader from "../../common/saas/SaaSSectionHeader";
import { getGroupedPartnerLeads } from "../../../api/partner/partnerFeatureApis";
import { getMeetingReminders } from "../../../api/customer/customerFeatureApis";
import "./PartnerDashboardView.css";

export const PartnerDashboardView = () => {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const assetUrl = process.env.REACT_APP_ASSET_URL || "";

  const [greeting, setGreeting] = useState("Good Morning");
  const [properties, setProperties] = useState([]);
  const [leadsCount, setLeadsCount] = useState(0);
  const [recentLeads, setRecentLeads] = useState([]);
  const [remindersCount, setRemindersCount] = useState(0);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Derive Display Name
  const userObj = loggedInUser?.user || loggedInUser?.userDetails || loggedInUser;
  const rawFirstName = userObj?.first_name || userObj?.firstName;
  const rawLastName = userObj?.last_name || userObj?.lastName;
  const rawName = userObj?.name || userObj?.customer_name;
  const fullName = [rawFirstName, rawLastName].filter(Boolean).join(" ") || rawName;
  const displayName = fullName && fullName.trim() && !fullName.toLowerCase().includes("null")
    ? fullName.trim()
    : "Partner";

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 18) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        // 1. Fetch Partner Properties
        try {
          const propRes = await axios.post(
            `${baseUrl}/property/list/preferred/v1`,
            { page_number: 1, page_size: 10, order_by: "DESC", keyword_search: "" },
            { headers }
          );
          const propList = propRes.data?.propertyAssets || propRes.data?.properties || [];
          if (propList.length > 0) {
            setProperties(propList);
          } else {
            const fallbackRes = await axios.post(
              `${baseUrl}/property/list`,
              { page_number: 1, page_size: 10, order_by: "DESC" },
              { headers }
            );
            setProperties(fallbackRes.data?.properties || fallbackRes.data?.propertyAssets || []);
          }
        } catch (e) {
          console.warn("Partner dashboard property fetch error:", e);
        }

        // 2. Fetch Partner Leads
        if (token) {
          try {
            const data = await getGroupedPartnerLeads(token, {
              page_number: 1,
              page_size: 6,
              order_by: "DESC",
            });
            const leadList = data?.groupedPartnerLeads || [];
            setRecentLeads(leadList);
            setLeadsCount(data?.totalRecord || leadList.length || 0);
          } catch (e) {
            console.warn("Partner leads fetch error:", e);
          }

          // 3. Fetch Meeting Reminders
          try {
            const remData = await getMeetingReminders(token, { pageNumber: 1, pageSize: 5 });
            const remList = remData?.reminders || remData?.data || [];
            setRemindersCount(remData?.totalRecord || remList.length || 0);
          } catch (e) {
            console.warn("Partner reminders fetch error:", e);
          }
        }

        // 4. Fetch Partner Promotions
        try {
          const promoRes = await axios.post(
            `${baseUrl}/promotion/list/preferred`,
            { page_number: 1, page_size: 6, order_by: "DESC", is_partner: true },
            { headers }
          );
          setPromotions(promoRes.data?.promotions || promoRes.data?.data || []);
        } catch (e) {
          console.warn("Partner promotions fetch error:", e);
        }
      } catch (err) {
        console.error("Partner dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token, baseUrl]);

  return (
    <div className="partner-dash-root">
      {/* 1. Header / Welcome Banner */}
      <div className="partner-dash-welcome">
        <div className="partner-dash-welcome__left">
          <div className="partner-dash-welcome__badge">
            <FiShield size={13} /> Verified Business Partner Portal
          </div>
          <h1 className="partner-dash-welcome__title">
            {greeting}, {displayName}!
          </h1>
          <p className="partner-dash-welcome__subtitle">
            Manage your verified property listings, track client leads, monitor appointments, and optimize your business growth.
          </p>
        </div>
        <div className="partner-dash-welcome__actions">
          <button
            type="button"
            className="btn btn-primary btn-sm px-3 py-2 rounded-3 fw-medium shadow-sm d-inline-flex align-items-center gap-2"
            onClick={() => navigate("/add-property")}
          >
            <FiPlus size={15} />
            <span>Add Listing</span>
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2"
            onClick={() => navigate("/partner/leads")}
          >
            <FiFileText size={15} className="text-primary" />
            <span>Partner Leads</span>
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2"
            onClick={() => navigate("/partner/subscription")}
          >
            <FiZap size={15} className="text-warning" />
            <span>Current Plan</span>
          </button>
        </div>
      </div>

      {/* 2. Real Partner KPI Cards */}
      {loading ? (
        <SaaSLoadingSkeleton type="stats" count={4} />
      ) : (
        <div className="row g-3">
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="Active Listings"
              value={properties.length > 0 ? `${properties.length}` : "0"}
              icon={<FiBriefcase />}
              color="blue"
              subtitle="Marketplace Properties"
              onClick={() => navigate("/partner/my-properties")}
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="Customer Leads"
              value={leadsCount || "0"}
              icon={<FiFileText />}
              color="green"
              subtitle="High-Intent Inquiries"
              onClick={() => navigate("/partner/leads")}
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="Partner Plan"
              value="Active Plan"
              icon={<FiZap />}
              color="amber"
              subtitle="Verified Membership"
              onClick={() => navigate("/partner/subscription")}
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SaaSStatCard
              title="Site Reminders"
              value={remindersCount || "0"}
              icon={<FiCalendar />}
              color="purple"
              subtitle="Scheduled Client Visits"
              onClick={() => navigate("/partner/reminders")}
            />
          </div>
        </div>
      )}

      {/* 3. Partner Subscription & KYC Compliance Overview */}
      <div className="row g-3">
        {/* Subscription Card */}
        <div className="col-12 col-lg-6">
          <div className="partner-dash-status-card">
            <div>
              <div className="partner-dash-status-card__header">
                <h3 className="partner-dash-status-card__title">
                  <FiZap className="text-warning" /> Membership & Current Plan
                </h3>
                <span className="badge bg-success bg-opacity-10 text-success px-3 py-1 rounded-pill">
                  Active Subscription
                </span>
              </div>
              <p className="text-muted small mb-3">
                Your partner account has full active access to respond to customer inquiries, post property listings, and dispatch WhatsApp lead alerts.
              </p>
              <div className="bg-light rounded-3 p-3 mb-3 border">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-semibold text-dark small">Plan Tier:</span>
                  <span className="badge bg-primary text-white">Standard Partner Membership</span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted small">Lead Privileges:</span>
                  <span className="text-success small fw-semibold">Unlimited Inquiries Included</span>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm px-3 rounded-2 fw-medium d-inline-flex align-items-center gap-2"
                onClick={() => navigate("/partner/subscription")}
              >
                <span>Manage Plan & Upgrades</span>
                <FiArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* KYC Compliance Card */}
        <div className="col-12 col-lg-6">
          <div className="partner-dash-status-card">
            <div>
              <div className="partner-dash-status-card__header">
                <h3 className="partner-dash-status-card__title">
                  <FiShield className="text-primary" /> KYC & Document Compliance
                </h3>
                <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill">
                  Partner Verified
                </span>
              </div>
              <p className="text-muted small mb-3">
                Compliance documents maintain your verified partner trust badge and ensure immediate lead delivery across India.
              </p>
              <div className="bg-light rounded-3 p-3 mb-3 border">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-semibold text-dark small">Mandatory Documents:</span>
                  <span className="text-primary small fw-semibold">
                    <FiCheckCircle size={14} className="text-success me-1" /> Aadhar & PAN
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted small">Trust Score:</span>
                  <span className="text-dark small fw-bold">100% Compliant</span>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-3 rounded-2 fw-medium d-inline-flex align-items-center gap-2"
                onClick={() => navigate("/partner/profile")}
              >
                <span>View Documents & Profile</span>
                <FiArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Partner Offerings & Tools Quick Grid */}
      <div className="partner-dash-section">
        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                <FiLayers className="text-primary" /> Partner Offerings & Hub Management
              </h6>
              <small className="text-muted">Manage your verified customer inquiries, catalogs, and store</small>
            </div>
          </div>
          <div className="row g-2">
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="partner-dash-hub-btn"
                onClick={() => navigate("/partner/leads")}
              >
                <div className="partner-dash-hub-icon bg-primary bg-opacity-10 text-primary">
                  <FiFileText size={20} />
                </div>
                <span className="partner-dash-hub-label">Partner Leads</span>
              </button>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="partner-dash-hub-btn"
                onClick={() => navigate("/partner/interior-design")}
              >
                <div className="partner-dash-hub-icon bg-info bg-opacity-10 text-info">
                  <FiLayers size={20} />
                </div>
                <span className="partner-dash-hub-label">Interior Catalogs</span>
              </button>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="partner-dash-hub-btn"
                onClick={() => navigate("/partner/local-bazaar")}
              >
                <div className="partner-dash-hub-icon bg-success bg-opacity-10 text-success">
                  <FiShoppingBag size={20} />
                </div>
                <span className="partner-dash-hub-label">Local Bazaar</span>
              </button>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="partner-dash-hub-btn"
                onClick={() => navigate("/partner/services")}
              >
                <div
                  className="partner-dash-hub-icon bg-opacity-10"
                  style={{ background: "#f3e8ff", color: "#7e22ce" }}
                >
                  <FiGrid size={20} />
                </div>
                <span className="partner-dash-hub-label">My Services</span>
              </button>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="partner-dash-hub-btn"
                onClick={() => navigate("/partner/reminders")}
              >
                <div className="partner-dash-hub-icon bg-danger bg-opacity-10 text-danger">
                  <FiCalendar size={20} />
                </div>
                <span className="partner-dash-hub-label">Site Reminders</span>
              </button>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <button
                className="partner-dash-hub-btn"
                onClick={() => navigate("/partner/subscription")}
              >
                <div className="partner-dash-hub-icon bg-warning bg-opacity-10 text-warning">
                  <FiZap size={20} />
                </div>
                <span className="partner-dash-hub-label">Current Plan</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Recent Property Listings Section */}
      <div className="partner-dash-section">
        <SaaSSectionHeader
          title="My Property Listings & Inventory"
          subtitle="Explore your active published listings and properties across operating regions"
          linkTo="/partner/my-properties"
          linkLabel="Manage Listings"
          badge={properties.length > 0 ? `${properties.length} Active` : null}
        />

        {loading ? (
          <SaaSLoadingSkeleton type="card" count={3} />
        ) : properties.length > 0 ? (
          <div className="saas-cards-horizontal-scroll">
            {properties.map((prop, idx) => {
              const imageSrc =
                prop.propertyImages?.[0]?.image_url
                  ? prop.propertyImages[0].image_url.startsWith("http")
                    ? prop.propertyImages[0].image_url
                    : `${assetUrl}${prop.propertyImages[0].image_url}`
                  : `https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&auto=format&fit=crop&q=80`;

              const budgetDisplay = prop.budget
                ? `₹${Number(prop.budget).toLocaleString("en-IN")}`
                : "Price on Request";

              return (
                <div
                  key={prop.id || idx}
                  className="saas-property-card"
                  onClick={() => navigate(`/partner/properties/${prop.id || ""}`)}
                >
                  <div className="saas-property-card__img-wrap">
                    <img src={imageSrc} alt={prop.title || "Property"} loading="lazy" />
                    <span className="saas-property-card__badge-purpose">
                      {prop.purpose_name || "Active Listing"}
                    </span>
                    <span className="saas-property-card__badge-price">{budgetDisplay}</span>
                  </div>
                  <div className="saas-property-card__body">
                    <h3 className="saas-property-card__title">
                      {prop.title || prop.property_name || "Modern Property Listing"}
                    </h3>
                    <p className="saas-property-card__location">
                      <FiMapPin size={13} className="text-primary flex-shrink-0" />
                      <span>
                        {[prop.area_name, prop.city_name, prop.state_name].filter(Boolean).join(", ") || "Prime Location"}
                      </span>
                    </p>
                    <div className="saas-property-card__specs">
                      {prop.type_name && (
                        <span className="saas-spec-pill">
                          <FiGrid size={12} /> {prop.type_name}
                        </span>
                      )}
                      {prop.sub_type_name && (
                        <span className="saas-spec-pill">
                          <FiMaximize size={12} /> {prop.sub_type_name}
                        </span>
                      )}
                      <span className="saas-spec-pill saas-spec-pill--highlight">
                        <FiCheckCircle size={12} /> Published
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <SaaSEmptyState
            icon={<FiBriefcase />}
            title="No Property Listings Yet"
            description="Create your first property listing to start presenting your properties to customers."
            actionLabel="Create Listing"
            actionLink="/add-property"
          />
        )}
      </div>

      {/* 6. Partner Customer Leads Section */}
      <div className="partner-dash-section">
        <SaaSSectionHeader
          title="Recent Customer Inquiries & Leads"
          subtitle="Direct verified inquiries from potential buyers and tenants interested in your properties"
          linkTo="/partner/leads"
          linkLabel="View All Leads"
          badge={recentLeads.length > 0 ? `${recentLeads.length} Inquiries` : null}
        />

        {loading ? (
          <SaaSLoadingSkeleton type="card" count={3} />
        ) : recentLeads.length > 0 ? (
          <div className="saas-cards-horizontal-scroll">
            {recentLeads.map((lead, idx) => {
              const customerCount = lead.customer_count || lead.customer_ids?.length || 1;
              const locationText = [lead.area, lead.city, lead.state].filter(Boolean).join(", ") || "Active Region";

              return (
                <div
                  key={idx}
                  className="saas-post-card"
                  onClick={() => navigate("/partner/leads")}
                >
                  <div className="saas-post-card__header">
                    <div className="saas-post-card__avatar bg-primary text-white">
                      {lead.lead_name?.charAt(0).toUpperCase() || "L"}
                    </div>
                    <div className="saas-post-card__meta">
                      <h4 className="saas-post-card__author text-truncate" style={{ maxWidth: "180px" }}>
                        {lead.lead_name || "Property Inquiry"}
                      </h4>
                      <span className="saas-post-card__tag">
                        {customerCount} {customerCount === 1 ? "Customer" : "Customers"}
                      </span>
                    </div>
                  </div>
                  <div className="saas-post-card__body">
                    <p className="saas-post-card__desc">
                      Client expresses interest in property #{lead.property_id || lead.post_id || "listing"}.
                    </p>
                    <div className="saas-post-card__location">
                      <FiMapPin size={12} /> {locationText}
                    </div>
                  </div>
                  <div className="saas-post-card__footer">
                    <button
                      type="button"
                      className="saas-btn saas-btn--primary saas-btn--sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/partner/leads");
                      }}
                    >
                      <span>Connect via WhatsApp</span>
                      <FiArrowRight size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <SaaSEmptyState
            icon={<FiFileText />}
            title="No Customer Inquiries Yet"
            description="New inquiries from interested customers will appear here."
            actionLabel="View Leads Hub"
            actionLink="/partner/leads"
          />
        )}
      </div>

      {/* 7. Active Promotions Section */}
      <div className="partner-dash-section">
        <SaaSSectionHeader
          title="Active Promotions & Campaigns"
          subtitle="Feature your properties with premium placement banners and high-intent alerts"
          linkTo="/partner/promotions"
          linkLabel="Manage Promotions"
        />

        {loading ? (
          <SaaSLoadingSkeleton type="card" count={3} />
        ) : promotions.length > 0 ? (
          <div className="saas-cards-horizontal-scroll">
            {promotions.map((promo, idx) => {
              const imageSrc =
                promo.propertyImages?.[0]?.image_url
                  ? promo.propertyImages[0].image_url.startsWith("http")
                    ? promo.propertyImages[0].image_url
                    : `${assetUrl}${promo.propertyImages[0].image_url}`
                  : promo.image_url || `https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&auto=format&fit=crop&q=80`;

              return (
                <div
                  key={promo.id || idx}
                  className="saas-promo-card"
                  onClick={() => navigate(`/partner/promotions/${promo.id || ""}`)}
                >
                  <div className="saas-promo-card__img-wrap">
                    <img src={imageSrc} alt={promo.title || "Promotion"} loading="lazy" />
                    <span className="saas-promo-card__tag">
                      <FiTag size={12} /> Active Campaign
                    </span>
                  </div>
                  <div className="saas-promo-card__body">
                    <h3 className="saas-promo-card__title">
                      {promo.title || promo.promotion_name || "Featured Property Campaign"}
                    </h3>
                    <p className="saas-promo-card__desc">
                      {promo.description
                        ? promo.description.replace(/<[^>]+>/g, "").substring(0, 110)
                        : "Promotional campaign delivering verified client impressions."}
                      ...
                    </p>
                    <div className="d-flex align-items-center justify-content-between mt-auto pt-2 border-top">
                      <span className="text-success fw-bold" style={{ fontSize: "0.8125rem" }}>
                        Live on Marketplace
                      </span>
                      <span className="saas-link-arrow">
                        Details <FiArrowRight size={13} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <SaaSEmptyState
            icon={<FiTag />}
            title="No Active Promotional Campaigns"
            description="Promote your listings with targeted campaigns to gain maximum customer visibility."
            actionLabel="Create Campaign"
            actionLink="/partner/promotions"
          />
        )}
      </div>
    </div>
  );
};

export default PartnerDashboardView;
